import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { SmileIcon, FrownIcon, MehIcon, SunIcon, CloudRainIcon } from 'lucide-react';
const MoodCheckIn: React.FC = () => {
  const {
    recordMood,
    userData
  } = useUser();
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Check if user has already logged mood today
  const hasLoggedMoodToday = () => {
    if (!userData.moodHistory || userData.moodHistory.length === 0) return false;
    const lastMoodEntry = userData.moodHistory[userData.moodHistory.length - 1];
    const lastMoodDate = new Date(lastMoodEntry.timestamp);
    const today = new Date();
    return lastMoodDate.getDate() === today.getDate() && lastMoodDate.getMonth() === today.getMonth() && lastMoodDate.getFullYear() === today.getFullYear();
  };
  const moods = [{
    value: 'joyful',
    label: 'Joyful',
    icon: SmileIcon,
    color: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-300',
    description: 'I feel light and happy'
  }, {
    value: 'content',
    label: 'Content',
    icon: SunIcon,
    color: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-300',
    description: 'I feel calm and at peace'
  }, {
    value: 'neutral',
    label: 'Neutral',
    icon: MehIcon,
    color: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-300',
    description: 'I feel okay'
  }, {
    value: 'sad',
    label: 'Sad',
    icon: FrownIcon,
    color: 'bg-indigo-100',
    textColor: 'text-indigo-800',
    borderColor: 'border-indigo-300',
    description: 'I feel down or blue'
  }, {
    value: 'overwhelmed',
    label: 'Overwhelmed',
    icon: CloudRainIcon,
    color: 'bg-purple-100',
    textColor: 'text-purple-800',
    borderColor: 'border-purple-300',
    description: 'I feel stressed or anxious'
  }];
  const handleSelectMood = (mood: string) => {
    setSelectedMood(mood);
  };
  const handleSubmit = () => {
    if (selectedMood) {
      recordMood(selectedMood);
      setIsSubmitted(true);
    }
  };
  // Get mood trends
  const getMoodTrend = () => {
    if (!userData.moodHistory || userData.moodHistory.length < 2) {
      return {
        trend: 'neutral',
        message: 'Not enough data to show trends yet'
      };
    }
    const moodValues = {
      joyful: 5,
      content: 4,
      neutral: 3,
      sad: 2,
      overwhelmed: 1
    };
    const recentMoods = userData.moodHistory.slice(-7); // Last 7 entries
    const avgMood = recentMoods.reduce((sum, entry) => sum + (moodValues[entry.mood as keyof typeof moodValues] || 3), 0) / recentMoods.length;
    if (avgMood > 4) {
      return {
        trend: 'positive',
        message: 'Your mood has been consistently positive lately!'
      };
    } else if (avgMood > 3) {
      return {
        trend: 'stable',
        message: 'Your mood has been relatively stable lately.'
      };
    } else {
      return {
        trend: 'challenging',
        message: 'You seem to be facing some challenges lately. Remember to be gentle with yourself.'
      };
    }
  };
  const alreadyCheckedIn = hasLoggedMoodToday();
  const moodTrend = getMoodTrend();
  return <div className="bg-white rounded-xl shadow-md p-6 h-full" data-id="element-188">
      <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-189">
        <div className="bg-indigo-100 p-2 rounded-full mr-2" data-id="element-190">
          <SmileIcon className="text-indigo-600" size={18} data-id="element-191" />
        </div>
        Mood Garden
      </h2>
      {alreadyCheckedIn || isSubmitted ? <div data-id="element-192">
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-4" data-id="element-193">
            <h3 className="font-medium text-indigo-800 mb-2" data-id="element-194">
              Today's Mood Check-in
            </h3>
            <p className="text-gray-700" data-id="element-195">
              Thank you for tending to your emotional garden today! Your
              awareness helps nurture your inner landscape.
            </p>
          </div>
          <div className="mb-4" data-id="element-196">
            <h3 className="font-medium text-gray-700 mb-2" data-id="element-197">Your Mood Trend</h3>
            <div className={`p-3 rounded-lg ${moodTrend.trend === 'positive' ? 'bg-green-50 text-green-800' : moodTrend.trend === 'stable' ? 'bg-blue-50 text-blue-800' : 'bg-purple-50 text-purple-800'}`} data-id="element-198">
              {moodTrend.message}
            </div>
          </div>
          <div data-id="element-199">
            <h3 className="font-medium text-gray-700 mb-2" data-id="element-200">Recent Moods</h3>
            <div className="flex space-x-2 overflow-x-auto pb-2" data-id="element-201">
              {userData.moodHistory?.slice(-5).map((entry, index) => {
            const mood = moods.find(m => m.value === entry.mood);
            const Icon = mood?.icon || MehIcon;
            return <div key={index} className={`flex-shrink-0 ${mood?.color} ${mood?.textColor} p-2 rounded-lg flex flex-col items-center`} data-id="element-202">
                    <Icon size={20} data-id="element-203" />
                    <span className="text-xs mt-1" data-id="element-204">{mood?.label}</span>
                    <span className="text-xs opacity-70" data-id="element-205">
                      {new Date(entry.timestamp).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}
                    </span>
                  </div>;
          })}
            </div>
          </div>
        </div> : <div data-id="element-206">
          <p className="text-gray-600 mb-4" data-id="element-207">
            How are you feeling today? Tracking your emotional landscape helps
            nurture your inner garden.
          </p>
          <div className="grid grid-cols-5 gap-2 mb-5" data-id="element-208">
            {moods.map(mood => {
          const Icon = mood.icon;
          return <button key={mood.value} onClick={() => handleSelectMood(mood.value)} className={`
                    flex flex-col items-center p-2 rounded-lg transition-all
                    ${mood.color} ${mood.textColor}
                    ${selectedMood === mood.value ? `ring-2 ring-indigo-500 ${mood.borderColor}` : 'border border-transparent hover:border-gray-200'}
                  `} data-id="element-209">
                  <Icon size={24} data-id="element-210" />
                  <span className="text-xs font-medium mt-1" data-id="element-211">{mood.label}</span>
                </button>;
        })}
          </div>
          {selectedMood && <div className="mb-4" data-id="element-212">
              <p className="text-gray-600 text-sm mb-2" data-id="element-213">
                {moods.find(mood => mood.value === selectedMood)?.description}
              </p>
            </div>}
          <button onClick={handleSubmit} disabled={!selectedMood} className={`
              w-full py-2 rounded-lg transition-colors
              ${selectedMood ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
            `} data-id="element-214">
            Record My Mood
          </button>
        </div>}
    </div>;
};
export default MoodCheckIn;